import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { formatCurrency } from "~/lib/format";
import type { FinancialLineItem } from "~/types/financials";

interface LineItemsTableProps {
	lineItems: FinancialLineItem[];
}

interface GroupedItems {
	section: string;
	items: FinancialLineItem[];
}

function groupBySection(items: FinancialLineItem[]): GroupedItems[] {
	const map = new Map<string, FinancialLineItem[]>();
	for (const item of items) {
		const existing = map.get(item.section);
		if (existing) {
			existing.push(item);
		} else {
			map.set(item.section, [item]);
		}
	}
	return Array.from(map.entries()).map(([section, items]) => ({ section, items }));
}

export function LineItemsTable({ lineItems }: LineItemsTableProps) {
	const groups = groupBySection(lineItems);
	const [expanded, setExpanded] = useState<Set<string>>(new Set(groups.map((g) => g.section)));

	if (lineItems.length === 0) {
		return (
			<p className="text-sm text-muted-foreground">
				Detailní položky nejsou k dispozici pro tento rok.
			</p>
		);
	}

	const toggleSection = (section: string) => {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(section)) {
				next.delete(section);
			} else {
				next.add(section);
			}
			return next;
		});
	};

	const hasBrutto = lineItems.some((li) => li.brutto != null);

	return (
		<div className="card-surface rounded-xl p-6">
			<h3 className="text-lg font-semibold mb-4">Detailní položky výkazu</h3>
			<div className="overflow-x-auto">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b">
							<th className="text-left py-2 pr-4 font-medium">Položka</th>
							<th className="text-right py-2 px-2 font-medium">Běžné období</th>
							<th className="text-right py-2 px-2 font-medium">Minulé období</th>
							{hasBrutto && (
								<>
									<th className="text-right py-2 px-2 font-medium">Brutto</th>
									<th className="text-right py-2 px-2 font-medium">Korekce</th>
									<th className="text-right py-2 px-2 font-medium">Netto</th>
								</>
							)}
						</tr>
					</thead>
					<tbody>
						{groups.map((group) => (
							<SectionGroup
								key={group.section}
								group={group}
								isExpanded={expanded.has(group.section)}
								onToggle={() => toggleSection(group.section)}
								hasBrutto={hasBrutto}
							/>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function SectionGroup({
	group,
	isExpanded,
	onToggle,
	hasBrutto,
}: {
	group: GroupedItems;
	isExpanded: boolean;
	onToggle: () => void;
	hasBrutto: boolean;
}) {
	return (
		<>
			<tr className="border-b bg-muted/50 cursor-pointer hover:bg-muted" onClick={onToggle}>
				<td className="py-2 pr-4 font-semibold" colSpan={hasBrutto ? 6 : 3}>
					<div className="flex items-center gap-1">
						{isExpanded ? (
							<ChevronDown className="h-4 w-4" />
						) : (
							<ChevronRight className="h-4 w-4" />
						)}
						{group.section}
					</div>
				</td>
			</tr>
			{isExpanded &&
				group.items.map((item) => (
					<tr key={item.id} className="border-b border-gray-100">
						<td className="py-1.5 pr-4 pl-6 text-muted-foreground">
							<span className="text-xs text-muted-foreground/60 mr-2">{item.lineCode}</span>
							{item.lineLabel}
						</td>
						<td className="text-right py-1.5 px-2 font-mono tabular-nums">
							{formatCurrency(item.currentPeriod)}
						</td>
						<td className="text-right py-1.5 px-2 font-mono tabular-nums">
							{formatCurrency(item.priorPeriod)}
						</td>
						{hasBrutto && (
							<>
								<td className="text-right py-1.5 px-2 font-mono tabular-nums">
									{formatCurrency(item.brutto)}
								</td>
								<td className="text-right py-1.5 px-2 font-mono tabular-nums">
									{formatCurrency(item.correction)}
								</td>
								<td className="text-right py-1.5 px-2 font-mono tabular-nums">
									{formatCurrency(item.netto)}
								</td>
							</>
						)}
					</tr>
				))}
		</>
	);
}
